import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSlotUpdates, useCalDateUpdates, useSceneUpdates } from '../../../../../hooks/dataUpdates';
import { SlotInfo, SlotType } from '../../../../../types/schedule.types';
import { SceneInfo } from '../../../../../types/scene.types';
import { ElementInfo, ElementSceneInfo, ElementType, getRestrictedDateNote } from '../../../../../types/element.types';
import type { CalDateInfo } from '../../../../../types/schedule.types';
import { getSceneCodeCellBackgroundColor } from '../../../../Breakdown/ScenesTable/Utils/SceneCellUtils';
import { ReactComponent as LocationIcon } from '../../../../../assets/svg/Schedule/location.svg';
import { ReactComponent as DeleteIcon } from '../../../../../assets/svg/Schedule/delete.svg';
import { ReactComponent as NoteIcon } from '../../../../../assets/svg/Schedule/note-solid.svg';
import { sortCastItemsByNO } from '../../../../../utils/elementSortUtils';
import {
  normalizeToSortedIds,
  buildDisplaySegments,
  slotShowsLocationHeader,
} from '../../../../../utils/calDateSlotsSortUtils';
import { useDataCache } from '../../../../../contexts/BreakdownDataCacheContext';
import { useRestrictedDatesCast } from '../../../../../hooks/useRestrictedDatesCast';
import { enrichElementRelations } from '../../../../../utils/enrichElementRelations';
import { syncNoteTextareaHeightToContent } from '../../../../../utils/noteTextareaAutosize';
import { CalendarInsertGapLine, CalendarRowInsertHalves } from './CalendarInsertDropZones';
import { createBundleAwareVerticalListSortingStrategy } from './bundleAwareVerticalListSortingStrategy';
import { buildSlotSummaryPreview } from './slotSummaryPreview';
import '../Calendar.css';

/** 编辑态摘要浮层：两行以内贴住锚点高度，超出后向下增高，接近视口底边时内部滚动 */
function syncSummaryEditorHeight(el: HTMLTextAreaElement, minHeight: number): void {
  const top = el.getBoundingClientRect().top;
  const maxHeight = Math.max(minHeight, window.innerHeight - top - 12);
  el.style.height = 'auto';
  const contentHeight = el.scrollHeight;
  if (contentHeight <= minHeight + 2) {
    el.style.height = `${minHeight}px`;
    el.style.overflowY = 'hidden';
    return;
  }
  const next = Math.min(contentHeight, maxHeight);
  el.style.height = `${next}px`;
  el.style.overflowY = contentHeight > maxHeight + 1 ? 'auto' : 'hidden';
}

export interface DayInfoProps {
  canEdit?: boolean;
  date: Date;
  slots: SlotInfo[];
  calDates?: CalDateInfo[];
  scenes: SceneInfo[];
  elementRelations: ElementSceneInfo[];
  breakdownID?: string;
  scheduleID?: string;
  onSlotDelete?: (slotID: string) => void;
  onSlotOrderChange?: () => void;
  disabledDateCells?: Set<string>;
  activeSortingId?: string | null;
  activeSortingType?: 'slot' | null;
  activeSortingDate?: string | null;
  activeSortingDragLevel?: 'slot' | 'note' | null;
  activeSlotDragBundleIds?: string[] | null;
  currentDate?: Date | null;
  shootingPeriod?: { totalDays: number } | null;
  getShootingDayNumber?: (date: Date) => number | null;
  onCurrentDateInfoChange?: (info: {
    shootingDayNumber: number | null;
    totalDays: number | null;
    shift: string | undefined;
    totalPages: number;
    featuredRolesQuantity: number;
    crowdRolesQuantity: number;
    cast: string;
  }) => void;
  /** 场次/跨日单卡：高亮对应插入缝（仅缝变色，非整卡） */
  calendarInsertHighlight?: { dateStr: string; insertIndex: number } | null;
  /** 启用插入缝与卡片半区命中（拖拽场次或跨日 slot 时） */
  enableCalendarInsertHitZones?: boolean;
  /** 外部插入悬停本日时禁用同日 sortable，减少与插入位抢碰撞 */
  disableInDaySortableForExternalInsert?: boolean;
  /** “显示所有地点”模式：UI 忽略 slotLocationVisibility */
  forceShowAllLocations?: boolean;
  /** 当本日 calDateSlotsSort 发生更新后，触发地点头归一化（按需在 showAllLocations=false 时执行） */
  normalizeDatesIfNeeded?: (dateStrs: string[]) => Promise<void>;
  updateCalDateSlotsSort?: (
    dateStr: string,
    additionalSoltIds?: string[],
    excludeSoltIds?: string[],
    replaceIds?: string[],
    calDateShift?: string,
  ) => Promise<void>;
  /** 与点击日历格一致：选中本日（格内控件交互时调用） */
  onSelectCalendarDay?: () => void;
  /** 整日拖拽时源日期 YYYY-MM-DD：该日所有卡片与单卡拖出一样隐藏（示意在 DragOverlay） */
  wholeDayDragSourceDateStr?: string | null;
}


/** 日历格内 NOTE/SHOOT 卡片须在模块作用域定义：若在 DayInfo 内声明，每次父组件重渲染都会产生新的组件类型，React 会卸载整个子树导致 textarea 反复挂载，输入立刻丢失 */
const SortableNoteCard: React.FC<{
  canEdit: boolean;
  slot: SlotInfo;
  dateStr: string;
  wholeDaySourceCardsHidden: boolean;
  onDelete: (e: React.MouseEvent, slotID: string) => void;
  isEditing?: boolean;
  /** 进入编辑时点击位置的 viewport X，用于插入符对齐鼠标（与日历摘要编辑一致） */
  noteEditClickX?: number | null;
  onStartEdit?: (slotID: string, content: string, clientX?: number) => void;
  onFinishEdit?: (content: string) => void;
  onCancelEdit?: () => void;
  /** 当日拍摄计划列表首行：便签条顶距与「首日首张带地点头 SHOOT」一致为 0 */
  isFirstRowInDay?: boolean;
}> = ({
  canEdit,
  slot,
  dateStr,
  wholeDaySourceCardsHidden,
  onDelete,
  isEditing,
  noteEditClickX,
  onStartEdit,
  onFinishEdit,
  onCancelEdit,
  isFirstRowInDay = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const syncNoteTextareaHeight = useCallback((el: HTMLTextAreaElement | null) => {
    syncNoteTextareaHeightToContent(el, 18);
  }, []);
  useLayoutEffect(() => {
    syncNoteTextareaHeight(inputRef.current);
  }, [slot.slotName, slot.slotID, isEditing, syncNoteTextareaHeight]);
  useEffect(() => {
    const el = inputRef.current;
    if (!el || isEditing) return;
    const next = (slot.slotName || '').replace(/\r\n/g, '\n');
    if (el.value !== next) {
      el.value = next;
      syncNoteTextareaHeight(el);
    }
  }, [slot.slotName, isEditing, syncNoteTextareaHeight]);
  useEffect(() => {
    if (!isEditing) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    const clientX = noteEditClickX;
    const text = input.value;
    if (clientX != null) {
      requestAnimationFrame(() => {
        if (!text.includes('\n')) {
          const rect = input.getBoundingClientRect();
          const style = getComputedStyle(input);
          const paddingLeft = parseFloat(style.paddingLeft) || 0;
          const x = Math.max(0, clientX - rect.left - paddingLeft);
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.font = style.font || `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
            let pos = text.length;
            for (let i = 0; i <= text.length; i++) {
              if (ctx.measureText(text.substring(0, i)).width >= x) {
                pos = i;
                break;
              }
            }
            input.setSelectionRange(pos, pos);
          }
        } else {
          input.setSelectionRange(text.length, text.length);
        }
      });
    }
  }, [isEditing, noteEditClickX]);
  useEffect(() => {
    if (!isEditing) return;
    const save = () => onFinishEdit?.(inputRef.current?.value.replace(/\r\n/g, '\n') ?? '');
    const onVisibilityChange = () => {
      if (document.hidden) save();
    };
    const onWindowBlur = () => save();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [isEditing, onFinishEdit]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: slot.slotID,
    data: {
      type: 'slot',
      slot,
      sourceDateStr: slot.slotDate?.split('T')[0] || '',
      dateStr,
      dragLevel: 'note',
    },
  });
  const cardDragHidden = isSortableDragging || wholeDaySourceCardsHidden;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: cardDragHidden ? 0 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dayInfo-slot-note-card ${isFirstRowInDay ? 'dayInfo-slot-note-card--first-row-in-day' : ''} ${cardDragHidden ? 'sortable-dragging' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="dayInfo-slot-bottom-info-note">
        <div className="dayInfo-slot-bottom-info-note-drag-handle" {...attributes} {...listeners}>
          <NoteIcon className="dayInfo-slot-bottom-info-note-icon" />
        </div>
        <textarea
          ref={inputRef}
          rows={1}
          className={`dayInfo-slot-bottom-info-note-input ${!isEditing ? 'dayInfo-slot-bottom-info-note-input-readonly' : ''}`}
          defaultValue={(slot.slotName || '').replace(/\r\n/g, '\n')}
          key={`note-${slot.slotID}`}
          readOnly={!isEditing}
          onBlur={() => isEditing && onFinishEdit?.(inputRef.current?.value.replace(/\r\n/g, '\n') ?? '')}
          onInput={(e) => isEditing && syncNoteTextareaHeight(e.currentTarget)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancelEdit?.();
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditing) onStartEdit?.(slot.slotID, slot.slotName || '', e.clientX);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
        {canEdit && isHovered && !isEditing && (
          <button
            className="dayInfo-slot-bottom-info-note-delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(e, slot.slotID);
            }}
            title="删除便签"
          >
            <DeleteIcon className="dayInfo-slot-bottom-info-note-delete-icon" />
          </button>
        )}
      </div>
    </div>
  );
};

const SortableIndependentSoltCard: React.FC<{
  canEdit: boolean;
  slot: SlotInfo;
  dateStr: string;
  wholeDaySourceCardsHidden: boolean;
  isFirstSoltCardInDay: boolean;
  showLocationHeader: boolean;
  getSceneInfo: (sceneID: string | undefined) => SceneInfo | undefined;
  onDelete: (e: React.MouseEvent, slotID: string) => void;
  editingSummarySlotID?: string | null;
  editingSummaryClickX?: number | null;
  onStartEditSummary?: (slotID: string, currentContent: string, clientX?: number) => void;
  onFinishEditSummary?: (content: string) => void | boolean | Promise<void | boolean>;
  onCancelEditSummary?: () => void;
  activeSortingType?: 'slot' | null;
  activeSortingDate?: string | null;
  activeSlotDragBundleIds?: string[] | null;
  onSelectCalendarDay?: () => void;
}> = ({
  canEdit,
  slot,
  dateStr,
  wholeDaySourceCardsHidden,
  isFirstSoltCardInDay,
  showLocationHeader,
  getSceneInfo,
  onDelete,
  editingSummarySlotID,
  editingSummaryClickX,
  onStartEditSummary,
  onFinishEditSummary,
  onCancelEditSummary,
  activeSortingType = null,
  activeSortingDate: sortingSourceDate = null,
  activeSlotDragBundleIds: bundleIds = null,
  onSelectCalendarDay: selectThisCalendarDay,
}) => {
  const { t } = useTranslation();
  const scene = getSceneInfo(slot.slotBelongSceneID);
  const location = (scene?.sceneLocation ?? '').trim() || t('schedule.locationNotSet');
  const pid = slot.slotID;
  const summaryDisplayRef = useRef<HTMLTextAreaElement>(null);
  const summaryEditorRef = useRef<HTMLTextAreaElement>(null);
  const summaryCaretRef = useRef<number | null>(null);
  const summaryCancelRef = useRef(false);
  const summaryClosedRef = useRef(false);
  const isComposingSummaryRef = useRef(false);
  const compositionEndedAtRef = useRef(0);
  const syncSummaryTextareaHeight = useCallback((el: HTMLTextAreaElement | null) => {
    syncNoteTextareaHeightToContent(el, 17);
  }, []);
  const isEditingSummary = editingSummarySlotID === pid;
  const [isHovered, setIsHovered] = useState(false);
  const summaryPreview = useMemo(
    () => buildSlotSummaryPreview(scene?.scenePlotSummary || ''),
    [scene?.scenePlotSummary],
  );
  void editingSummaryClickX;

  const placeSummaryEditor = useCallback(() => {
    const anchor = summaryDisplayRef.current;
    const editor = summaryEditorRef.current;
    if (!anchor || !editor) return;
    const rect = anchor.getBoundingClientRect();
    const scroller = anchor.closest('.calendar-days-container');
    let hidden = rect.width <= 0 || rect.height <= 0;
    if (scroller) {
      const scrollerRect = scroller.getBoundingClientRect();
      const header = scroller.querySelector('.calendar-weekday-row--sticky');
      const visibleTop = header?.getBoundingClientRect().bottom ?? scrollerRect.top;
      hidden = hidden || rect.top < visibleTop || rect.bottom <= scrollerRect.top || rect.top >= scrollerRect.bottom;
    }
    editor.style.top = `${rect.top}px`;
    editor.style.left = `${rect.left}px`;
    editor.style.width = `${rect.width}px`;
    editor.style.visibility = hidden ? 'hidden' : 'visible';
    editor.style.pointerEvents = hidden ? 'none' : 'auto';
    return rect.height;
  }, []);

  const commitSummaryEdit = useCallback(() => {
    if (summaryCancelRef.current || summaryClosedRef.current) return;
    summaryClosedRef.current = true;
    const editor = summaryEditorRef.current;
    const value = editor?.value ?? '';
    if (editor && document.activeElement === editor) editor.blur();
    void Promise.resolve(onFinishEditSummary?.(value)).then((saved) => {
      if (saved === false) summaryClosedRef.current = false;
    });
  }, [onFinishEditSummary]);

  const cancelSummaryEdit = useCallback(() => {
    if (summaryCancelRef.current || summaryClosedRef.current) return;
    summaryCancelRef.current = true;
    onCancelEditSummary?.();
  }, [onCancelEditSummary]);

  useLayoutEffect(() => {
    const el = summaryDisplayRef.current;
    if (!el) return;
    syncSummaryTextareaHeight(el);
    el.scrollTop = 0;
  }, [summaryPreview, pid, syncSummaryTextareaHeight]);

  useLayoutEffect(() => {
    if (!isEditingSummary) return;
    summaryCancelRef.current = false;
    summaryClosedRef.current = false;
    const minHeight = placeSummaryEditor();
    const editor = summaryEditorRef.current;
    if (!editor || minHeight == null) return;
    syncSummaryEditorHeight(editor, minHeight);
    editor.focus();
    const len = editor.value.length;
    const caret = summaryCaretRef.current;
    const pos = caret == null ? len : Math.max(0, Math.min(caret, len));
    editor.setSelectionRange(pos, pos);
  }, [isEditingSummary, placeSummaryEditor]);

  useEffect(() => {
    if (!isEditingSummary) return;
    let raf = 0;
    let lastWidth = Math.round(summaryEditorRef.current?.getBoundingClientRect().width || -1);
    const tick = () => {
      const editor = summaryEditorRef.current;
      const minHeight = placeSummaryEditor();
      if (editor && minHeight != null) {
        const width = Math.round(editor.getBoundingClientRect().width);
        if (width !== lastWidth) {
          lastWidth = width;
          syncSummaryEditorHeight(editor, minHeight);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isEditingSummary, placeSummaryEditor]);

  useEffect(() => {
    if (!isEditingSummary) return;
    const onPointerDown = (event: PointerEvent) => {
      const editor = summaryEditorRef.current;
      if (editor && event.target instanceof Node && editor.contains(event.target)) return;
      commitSummaryEdit();
    };
    const onVisibilityChange = () => {
      if (document.hidden) commitSummaryEdit();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', commitSummaryEdit);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', commitSummaryEdit);
    };
  }, [isEditingSummary, commitSummaryEdit]);

  const backgroundColor = getSceneCodeCellBackgroundColor(scene?.sceneInteriorExterior, scene?.sceneDayNight);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: pid,
    data: {
      type: 'slot',
      slot,
      sourceDateStr: slot.slotDate?.split('T')[0] || '',
      dateStr,
      dragLevel: 'slot',
    },
  });

  const inBundledDragHidden =
    activeSortingType === 'slot' &&
    sortingSourceDate === dateStr &&
    (bundleIds?.length ?? 0) > 0 &&
    !!bundleIds?.includes(pid);

  const dragSourceHidden = isSortableDragging || inBundledDragHidden || wholeDaySourceCardsHidden;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`dayInfo-slot-standalone-card ${isFirstSoltCardInDay ? 'dayInfo-slot-standalone-card--first-in-day' : ''} ${dragSourceHidden ? 'sortable-dragging' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showLocationHeader ? (
        <div className="dayInfo-slot-card-header">
          <div
            className="dayInfo-slot-card-header-drag-bundle"
            data-slot-drag-handle="bundle"
            {...attributes}
            {...listeners}
          >
            <div className="dayInfo-slot-card-location">
              <LocationIcon className="dayInfo-slot-card-location-icon" aria-label="地点" />
              <span className="dayInfo-slot-card-location-text" key={`location-${pid}`}>
                {location}
              </span>
            </div>
          </div>
        </div>
      ) : null}
      <div className="dayInfo-slot-card">
        <div
          className="dayInfo-slot-card-sceneCode"
          style={{ '--dynamic-bg-color': backgroundColor } as React.CSSProperties}
          data-slot-drag-handle="single"
          {...attributes}
          {...listeners}
        >
          {scene?.sceneCode || ''}
        </div>
        <textarea
          ref={summaryDisplayRef}
          rows={1}
          className={`dayInfo-slot-card-summary-input dayInfo-slot-card-summary-input-readonly${isEditingSummary ? ' dayInfo-slot-card-summary-input-editing' : ''}`}
          value={summaryPreview.preview}
          key={`summary-${pid}`}
          readOnly
          aria-hidden={isEditingSummary}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditingSummary) {
              const sel = e.currentTarget.selectionStart ?? summaryPreview.preview.length;
              const map = summaryPreview.caretMap;
              summaryCaretRef.current = map[Math.max(0, Math.min(sel, map.length - 1))] ?? 0;
              selectThisCalendarDay?.();
              onStartEditSummary?.(pid, scene?.scenePlotSummary || '', e.clientX);
            }
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
        {isEditingSummary && createPortal(
          <textarea
            ref={summaryEditorRef}
            rows={1}
            className="dayInfo-slot-card-summary-editor"
            defaultValue={scene?.scenePlotSummary || ''}
            aria-label={t('scene.plotSummary')}
            onCompositionStart={() => {
              isComposingSummaryRef.current = true;
            }}
            onCompositionEnd={(e) => {
              isComposingSummaryRef.current = false;
              compositionEndedAtRef.current = performance.now();
              syncSummaryEditorHeight(e.currentTarget, summaryDisplayRef.current?.getBoundingClientRect().height || 0);
            }}
            onInput={(e) => {
              const minHeight = summaryDisplayRef.current?.getBoundingClientRect().height || 0;
              syncSummaryEditorHeight(e.currentTarget, minHeight);
            }}
            onKeyDown={(e) => {
              const nativeIsComposing = Boolean((e.nativeEvent as KeyboardEvent).isComposing) || e.key === 'Process';
              if (e.key === 'Escape') {
                if (isComposingSummaryRef.current || nativeIsComposing) return;
                e.preventDefault();
                cancelSummaryEdit();
                return;
              }
              if (e.key !== 'Enter') return;
              if (isComposingSummaryRef.current || nativeIsComposing) return;
              // 选字用的回车会紧跟 compositionend，这次不结束编辑
              if (performance.now() - compositionEndedAtRef.current < 80) return;
              e.preventDefault();
              commitSummaryEdit();
            }}
            onBlur={() => {
              commitSummaryEdit();
            }}
          />,
          document.body
        )}
        {canEdit && isHovered && !isEditingSummary && (
          <button
            className="dayInfo-slot-card-delete-btn dayInfo-slot-card-delete-btn-in-card"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(e, pid);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title="删除"
          >
            <DeleteIcon className="dayInfo-slot-card-delete-icon" aria-label="移除" />
          </button>
        )}
      </div>
    </div>
  );
};


const DayInfo: React.FC<DayInfoProps> = ({
  canEdit = false,
  date,
  slots,
  calDates = [],
  scenes,
  elementRelations: propsElementRelations,
  breakdownID,
  scheduleID,
  onSlotDelete,
  onSlotOrderChange,
  disabledDateCells = new Set(),
  activeSortingId = null,
  activeSortingType = null,
  activeSortingDate = null,
  activeSortingDragLevel = null,
  activeSlotDragBundleIds = null,
  currentDate = null,
  shootingPeriod = null,
  getShootingDayNumber: getShootingDayNumberProp,
  onCurrentDateInfoChange,
  calendarInsertHighlight = null,
  enableCalendarInsertHitZones = false,
  disableInDaySortableForExternalInsert = false,
  forceShowAllLocations = false,
  normalizeDatesIfNeeded,
  updateCalDateSlotsSort,
  onSelectCalendarDay,
  wholeDayDragSourceDateStr = null,
}) => {
  const { t } = useTranslation();
  const { getBreakdownCache, subscribeBreakdownCache, getElementRelationCache, subscribeElementRelationCache, getElementCache, subscribeElementCache, getSlotCache } = useDataCache();
  const { getRestrictedDatesCastIdsForDate } = useRestrictedDatesCast(breakdownID ?? undefined);
  // getSlotCache 在 useCallback 内部使用，ESLint 可能误报
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unusedGetSlotCache = getSlotCache;
  void _unusedGetSlotCache;
  const { updateSlotData, deleteSlotData } = useSlotUpdates();
  const { upsertCalDateData } = useCalDateUpdates();
  const { updateScenesData } = useSceneUpdates();

  const [enrichedRelations, setEnrichedRelations] = useState<ElementSceneInfo[]>(propsElementRelations || []);
  const [cachedScenesRows, setCachedScenesRows] = useState<SceneInfo[]>(scenes);

  // 从缓存获取并组装数据
  useEffect(() => {
    if (!breakdownID) {
      setEnrichedRelations(propsElementRelations || []);
      setCachedScenesRows(scenes);
      return;
    }

    const updateData = () => {
      const breakdownCache = getBreakdownCache(breakdownID);
      const relationCache = getElementRelationCache(breakdownID);
      const itemCache = getElementCache(breakdownID);

      if (breakdownCache) {
        if (breakdownCache.scenes && breakdownCache.scenes.length > 0) {
          setCachedScenesRows(breakdownCache.scenes);
        }
      }

      if (relationCache?.elementRelations && itemCache?.elements) {
        const enriched = enrichElementRelations(
          relationCache.elementRelations,
          itemCache.elements
        );
        setEnrichedRelations(enriched);
      } else {
        setEnrichedRelations(propsElementRelations || []);
      }

      if (!breakdownCache) {
        setEnrichedRelations(propsElementRelations || []);
        setCachedScenesRows(scenes);
      }
    };

    updateData();
    const unsubscribeBreakdown = subscribeBreakdownCache(breakdownID, updateData);
    const unsubscribeRelations = subscribeElementRelationCache(breakdownID, updateData);
    const unsubscribeElements = subscribeElementCache(breakdownID, updateData);

    return () => {
      unsubscribeBreakdown();
      unsubscribeRelations();
      unsubscribeElements();
    };
  }, [breakdownID, propsElementRelations, scenes, getBreakdownCache, getElementRelationCache, getElementCache, subscribeBreakdownCache, subscribeElementRelationCache, subscribeElementCache]);

  const elementRelations = enrichedRelations;
  const scenesToUse = cachedScenesRows.length > 0 ? cachedScenesRows : scenes;
  const dateStr = useMemo(() => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [date]);

  const isDisabled = disabledDateCells.has(dateStr);
  const wholeDaySourceCardsHidden =
    wholeDayDragSourceDateStr != null && wholeDayDragSourceDateStr === dateStr;

  const calDate = useMemo(() => {
    return calDates.find(pd => {
      if (!pd.calDateDt) return false;
      const pdDateStr = pd.calDateDt.split('T')[0];
      return pdDateStr === dateStr;
    });
  }, [calDates, dateStr]);

  const [editingNoteSlotID, setEditingNoteSlotID] = useState<string | null>(null);
  const [editingNoteClickX, setEditingNoteClickX] = useState<number | null>(null);
  const [editingSummarySlotID, setEditingSummarySlotID] = useState<string | null>(null);
  const [editingSummaryClickX, setEditingSummaryClickX] = useState<number | null>(null);

  const handleUpdateNote = async (slotID: string, content: string) => {
    if (!scheduleID) return;
    try {
      await updateSlotData(scheduleID, slotID, { slotName: content });
      setEditingNoteSlotID(null);
      setEditingNoteClickX(null);
      if (onSlotOrderChange) onSlotOrderChange();
    } catch (error) {
      console.error('更新便签失败:', error);
      alert(t('calendar.updateNoteFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  const daySlots = useMemo(() => {
    return slots.filter(slot => {
      if (!slot.slotDate) return false;
      const slotDateStr = slot.slotDate.split('T')[0];
      return slotDateStr === dateStr;
    });
  }, [slots, dateStr]);

  const handleFinishEditNote = async (content: string) => {
    if (!editingNoteSlotID) return;
    const slot = daySlots.find(t => t.slotID === editingNoteSlotID);
    if (!slot) return;
    const trimmed = content.trim();
    if (trimmed !== (slot.slotName || '')) {
      await handleUpdateNote(editingNoteSlotID, trimmed);
    } else {
      setEditingNoteSlotID(null);
      setEditingNoteClickX(null);
    }
  };

  const handleCancelEditNote = () => {
    setEditingNoteSlotID(null);
    setEditingNoteClickX(null);
  };

  const calendarRowItems = useMemo(() => {
    const slotMap = new Map(daySlots.map((t) => [t.slotID, t]));
    const segments = buildDisplaySegments(
      daySlots,
      calDate?.calDateSlotsSort,
      dateStr,
      (sceneID) => {
        const s = scenesToUse.find((sc) => sc.sceneID === sceneID);
        return s ? { sceneLocation: s.sceneLocation } : undefined;
      },
      { forceShowAllLocations: forceShowAllLocations },
    );
    const rows: Array<{ kind: 'note' | 'slot'; slot: SlotInfo }> = [];
    for (const seg of segments) {
      for (const id of seg.items) {
        const t = slotMap.get(id);
        if (!t) continue;
        if (seg.kind === 'note') {
          rows.push({ kind: 'note', slot: t });
        } else {
          rows.push({ kind: 'slot', slot: t });
        }
      }
    }
    return rows;
  }, [daySlots, calDate?.calDateSlotsSort, dateStr, scenesToUse, forceShowAllLocations]);

  const getSceneInfo = useCallback((sceneID: string | undefined): SceneInfo | undefined => {
    if (!sceneID) return undefined;
    return scenesToUse.find(scene => scene.sceneID === sceneID);
  }, [scenesToUse]);

  const handleFinishEditSummary = async (content: string): Promise<boolean> => {
    if (!scheduleID || !editingSummarySlotID || !breakdownID) return false;
    const slot = daySlots.find((t) => t.slotID === editingSummarySlotID);
    if (!slot || !slot.slotBelongSceneID) return false;
    const newContent = content.trim();
    const scene = scenesToUse.find((s) => s.sceneID === slot.slotBelongSceneID);
    const oldContent = scene?.scenePlotSummary || '';
    if (newContent === oldContent) {
      setEditingSummarySlotID(null);
      setEditingSummaryClickX(null);
      return true;
    }
    try {
      await updateScenesData(
        [{ sceneId: slot.slotBelongSceneID, data: { scenePlotSummary: newContent } }],
        breakdownID
      );
      setEditingSummarySlotID(null);
      setEditingSummaryClickX(null);
      if (onSlotOrderChange) onSlotOrderChange();
      return true;
    } catch (error) {
      console.error('更新摘要失败:', error);
      alert(t('calendar.updateSummaryFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
      return false;
    }
  };

  const handleCancelEditSummary = () => {
    setEditingSummarySlotID(null);
    setEditingSummaryClickX(null);
  };

  const daySoltSlots = useMemo(() => {
    return daySlots.filter(
      t => t.slotType === SlotType.SHOOT || !t.slotType
    );
  }, [daySlots]);

  const calDateSortedIds = useMemo(() => {
    return normalizeToSortedIds(calDate?.calDateSlotsSort);
  }, [calDate?.calDateSlotsSort]);

  const castRelations = useMemo(() => {
    const sceneIDs = daySoltSlots
      .map(slot => slot.slotBelongSceneID)
      .filter((id): id is string => !!id);
    const castRels = elementRelations.filter(rel =>
      rel.elementType === ElementType.CAST &&
      sceneIDs.includes(rel.sceneID)
    );
    const uniqueCastRels = new Map<string, ElementSceneInfo>();
    castRels.forEach(rel => {
      if (rel.elementID && !uniqueCastRels.has(rel.elementID)) {
        uniqueCastRels.set(rel.elementID, rel);
      }
    });
    return sortCastItemsByNO(Array.from(uniqueCastRels.values()));
  }, [daySoltSlots, elementRelations]);

  const cast = useMemo(
    () => castRelations.map(rel => rel.elementAbbr).filter((abbr): abbr is string => !!abbr).join('/'),
    [castRelations]
  );

  const restrictedDatesCastIdsForThisDate = useMemo(
    () => getRestrictedDatesCastIdsForDate(dateStr),
    [getRestrictedDatesCastIdsForDate, dateStr]
  );

  /** 该日所有「受限日期」的主要角色（来自 elements，与当日场次任务无交集限制），有任务日与空日同一数据源 */
  const restrictedDatesCastItemsForThisDate = useMemo((): ElementInfo[] => {
    if (!breakdownID || restrictedDatesCastIdsForThisDate.size === 0) return [];
    const cache = getElementCache(breakdownID);
    const elements = cache?.elements ?? [];
    const items = elements.filter(
      (item): item is ElementInfo =>
        !!item.elementID &&
        item.elementType === ElementType.CAST &&
        !!item.elementAbbr &&
        restrictedDatesCastIdsForThisDate.has(item.elementID)
    );
    return sortCastItemsByNO(items);
  }, [breakdownID, restrictedDatesCastIdsForThisDate, getElementCache]);

  const elementById = useMemo(() => {
    if (!breakdownID) return new Map<string, ElementInfo>();
    const elements = getElementCache(breakdownID)?.elements ?? [];
    return new Map(
      elements
        .filter((item): item is ElementInfo => !!item.elementID)
        .map(item => [item.elementID, item])
    );
  }, [breakdownID, getElementCache]);

  const featuredRolesQuantity = useMemo(() => {
    const sceneIDs = daySoltSlots
      .map(slot => slot.slotBelongSceneID)
      .filter((id): id is string => !!id);
    const featuredRoleRelations = elementRelations.filter(rel =>
      rel.elementType === ElementType.FEATURED &&
      sceneIDs.includes(rel.sceneID)
    );
    const maxQtyByElementID = new Map<string, number>();
    featuredRoleRelations.forEach(rel => {
      if (rel.elementID) {
        const currentMax = maxQtyByElementID.get(rel.elementID) || 0;
        const qty = rel.elementQtyInScene || 1;
        maxQtyByElementID.set(rel.elementID, Math.max(currentMax, qty));
      }
    });
    let total = 0;
    maxQtyByElementID.forEach(maxQty => { total += maxQty; });
    return total;
  }, [daySoltSlots, elementRelations]);

  const crowdRolesQuantity = useMemo(() => {
    const sceneIDs = daySoltSlots
      .map(slot => slot.slotBelongSceneID)
      .filter((id): id is string => !!id);
    const crowdRoleRelations = elementRelations.filter(rel =>
      rel.elementType === ElementType.BACKGROUND &&
      sceneIDs.includes(rel.sceneID)
    );
    const maxQtyByElementID = new Map<string, number>();
    crowdRoleRelations.forEach(rel => {
      if (rel.elementID) {
        const currentMax = maxQtyByElementID.get(rel.elementID) || 0;
        const qty = rel.elementQtyInScene || 1;
        maxQtyByElementID.set(rel.elementID, Math.max(currentMax, qty));
      }
    });
    let total = 0;
    maxQtyByElementID.forEach(maxQty => { total += maxQty; });
    return total;
  }, [daySoltSlots, elementRelations]);

  const totalPages = useMemo(() => {
    const sceneIDs = daySoltSlots
      .map(slot => slot.slotBelongSceneID)
      .filter((id): id is string => !!id);
    const uniqueSceneIDs = Array.from(new Set(sceneIDs));
    let total = 0;
    uniqueSceneIDs.forEach(sceneID => {
      const scene = getSceneInfo(sceneID);
      if (scene && scene.scenePagesNumber !== undefined && scene.scenePagesNumber !== null) {
        const pages = Number(scene.scenePagesNumber);
        if (!isNaN(pages)) total += pages;
      }
    });
    return total;
  }, [daySoltSlots, getSceneInfo]);

  const prevInfoRef = useRef<{
    shootingDayNumber: number | null;
    totalDays: number | null;
    shift: string | undefined;
    totalPages: number;
    featuredRolesQuantity: number;
    crowdRolesQuantity: number;
    cast: string;
  } | null>(null);
  const callbackRef = useRef(onCurrentDateInfoChange);

  useEffect(() => {
    callbackRef.current = onCurrentDateInfoChange;
  }, [onCurrentDateInfoChange]);

  useEffect(() => {
    if (!currentDate || !callbackRef.current) return;
    const isCurrentDate = currentDate.getFullYear() === date.getFullYear() &&
                          currentDate.getMonth() === date.getMonth() &&
                          currentDate.getDate() === date.getDate();
    if (!isCurrentDate) return;
    const shootingDayNumber = getShootingDayNumberProp ? getShootingDayNumberProp(date) : null;
    const newInfo = {
      shootingDayNumber,
      totalDays: shootingPeriod?.totalDays || null,
      shift: calDate?.calDateShift,
      totalPages,
      featuredRolesQuantity,
      crowdRolesQuantity,
      cast,
    };
    const prevInfo = prevInfoRef.current;
    if (prevInfo &&
        prevInfo.shootingDayNumber === newInfo.shootingDayNumber &&
        prevInfo.totalDays === newInfo.totalDays &&
        prevInfo.shift === newInfo.shift &&
        prevInfo.totalPages === newInfo.totalPages &&
        prevInfo.featuredRolesQuantity === newInfo.featuredRolesQuantity &&
        prevInfo.crowdRolesQuantity === newInfo.crowdRolesQuantity &&
        prevInfo.cast === newInfo.cast) {
      return;
    }
    prevInfoRef.current = newInfo;
    callbackRef.current(newInfo);
  }, [currentDate, date, shootingPeriod, calDate, totalPages, featuredRolesQuantity, crowdRolesQuantity, cast, getShootingDayNumberProp]);

  const handleDelete = async (e: React.MouseEvent, slotID: string) => {
    e.stopPropagation();
    try {
      const slotToDelete = daySlots.find(t => t.slotID === slotID);
      const slotDateStr = slotToDelete?.slotDate?.split('T')[0];
      const slotScheduleID = slotToDelete?.slotBelongScheduleID;
      if (!slotScheduleID) {
        console.error('无法获取 scheduleID，无法删除 slot');
        return;
      }
      await deleteSlotData(slotScheduleID, slotID);
      if (slotDateStr && scheduleID) {
        const newIds = calDateSortedIds.filter((id) => id !== slotID);
        if (updateCalDateSlotsSort) {
          await updateCalDateSlotsSort(slotDateStr, undefined, undefined, newIds);
        } else {
          await upsertCalDateData(scheduleID, {
            calDateDt: slotDateStr,
            calDateBelongScheduleID: scheduleID,
            calDateSlotsSort: newIds,
          });
          await normalizeDatesIfNeeded?.([slotDateStr]);
        }
      }
      if (onSlotDelete) onSlotDelete(slotID);
    } catch (error) {
      console.error('删除拍摄任务失败:', error);
      alert(t('calendar.deleteFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };

  const hasSoltSlot = daySoltSlots.length > 0;
  const topLevelSortableIds = calendarRowItems.map((r) => r.slot.slotID);
  const firstSoltRowIndex = calendarRowItems.findIndex((r) => r.kind !== 'note');
  const sortableDisabled = isDisabled || disableInDaySortableForExternalInsert;

  const inDaySortableStrategy =
    activeSortingType === 'slot' &&
    activeSortingDate === dateStr &&
    activeSlotDragBundleIds &&
    activeSlotDragBundleIds.length > 1
      ? createBundleAwareVerticalListSortingStrategy(topLevelSortableIds, activeSlotDragBundleIds)
      : verticalListSortingStrategy;

  const gapIsActive = (insertIndex: number) =>
    !!calendarInsertHighlight &&
    calendarInsertHighlight.dateStr === dateStr &&
    calendarInsertHighlight.insertIndex === insertIndex;

  return (
    <div className="calendar-day-dayInfo">
      <SortableContext
        items={topLevelSortableIds}
        strategy={inDaySortableStrategy}
        disabled={sortableDisabled}
      >
        <div className="dayInfo-slot-cards">
          {calendarRowItems.length === 0 ? (
            <CalendarInsertGapLine
              dateStr={dateStr}
              insertIndex={0}
              isActive={gapIsActive(0)}
              zonesEnabled={enableCalendarInsertHitZones}
            />
          ) : (
            calendarRowItems.map((row, rowIndex) => (
              <React.Fragment key={row.slot.slotID}>
                <CalendarInsertGapLine
                  dateStr={dateStr}
                  insertIndex={rowIndex}
                  isActive={gapIsActive(rowIndex)}
                  zonesEnabled={enableCalendarInsertHitZones}
                />
                <div className="dayInfo-slot-sortable-row-wrap">
                  <CalendarRowInsertHalves
                    dateStr={dateStr}
                    rowIndex={rowIndex}
                    zonesEnabled={enableCalendarInsertHitZones}
                  />
                  {row.kind === 'note' ? (
                    <SortableNoteCard
                      canEdit={canEdit}
                      slot={row.slot}
                      dateStr={dateStr}
                      wholeDaySourceCardsHidden={wholeDaySourceCardsHidden}
                      onDelete={handleDelete}
                      isEditing={editingNoteSlotID === row.slot.slotID}
                      noteEditClickX={editingNoteClickX}
                      onStartEdit={(id, _content, clientX) => {
                        setEditingNoteSlotID(id);
                        setEditingNoteClickX(clientX ?? null);
                      }}
                      onFinishEdit={handleFinishEditNote}
                      onCancelEdit={handleCancelEditNote}
                      isFirstRowInDay={rowIndex === 0}
                    />
                  ) : (
                    <SortableIndependentSoltCard
                      canEdit={canEdit}
                      slot={row.slot}
                      dateStr={dateStr}
                      wholeDaySourceCardsHidden={wholeDaySourceCardsHidden}
                      isFirstSoltCardInDay={rowIndex === firstSoltRowIndex}
                      showLocationHeader={slotShowsLocationHeader(row.slot, { forceShowAllLocations: forceShowAllLocations })}
                      getSceneInfo={getSceneInfo}
                      onDelete={handleDelete}
                      editingSummarySlotID={editingSummarySlotID}
                      editingSummaryClickX={editingSummaryClickX}
                      onStartEditSummary={(id, _content, clientX) => {
                        setEditingSummarySlotID(id);
                        setEditingSummaryClickX(clientX ?? null);
                      }}
                      onFinishEditSummary={handleFinishEditSummary}
                      onCancelEditSummary={handleCancelEditSummary}
                      activeSortingType={activeSortingType}
                      activeSortingDate={activeSortingDate}
                      activeSlotDragBundleIds={activeSlotDragBundleIds}
                      onSelectCalendarDay={onSelectCalendarDay}
                    />
                  )}
                </div>
              </React.Fragment>
            ))
          )}
          {calendarRowItems.length > 0 && (
            <CalendarInsertGapLine
              dateStr={dateStr}
              insertIndex={calendarRowItems.length}
              isActive={gapIsActive(calendarRowItems.length)}
              zonesEnabled={enableCalendarInsertHitZones}
            />
          )}
        </div>
      </SortableContext>

      <div className="dayInfo-slot-bottom-info">
        {hasSoltSlot && (
          <div className="dayInfo-slot-bottom-info-stats">
            {(castRelations.length > 0 || restrictedDatesCastItemsForThisDate.length > 0) && (
              <div className="dayInfo-slot-cast">
                {castRelations.map((rel, i) => {
                  const isRestrictedOnThisDate = restrictedDatesCastIdsForThisDate.has(rel.elementID!);
                  const restrictedNote = rel.elementID
                    ? getRestrictedDateNote(
                        elementById.get(rel.elementID)?.elementRestrictedDates,
                        dateStr
                      )
                    : '';
                  return (
                  <React.Fragment key={rel.elementID}>
                    {i > 0 && '/'}
                    <span
                      className={
                        isRestrictedOnThisDate
                          ? 'dayInfo-slot-cast-restrictedDates'
                          : restrictedNote
                            ? 'dayInfo-slot-cast-hasNote'
                            : ''
                      }
                      title={restrictedNote || undefined}
                    >
                      {rel.elementAbbr}
                    </span>
                  </React.Fragment>
                  );
                })}
                {restrictedDatesCastItemsForThisDate.length > 0 && (
                  <span
                    className={
                      'dayInfo-slot-cast-restrictedBracketGroup' +
                        (castRelations.length > 0
                        ? ' dayInfo-slot-cast-restrictedBracketGroup--afterMain'
                        : '')
                    }
                  >
                    [
                    {restrictedDatesCastItemsForThisDate.map((item, i) => {
                      const restrictedNote = getRestrictedDateNote(item.elementRestrictedDates, dateStr);
                      return (
                      <React.Fragment key={item.elementID}>
                        {i > 0 && '/'}
                        <span
                          className="dayInfo-slot-cast-restrictedBracketItem"
                          title={restrictedNote || undefined}
                        >
                          {item.elementAbbr}
                        </span>
                      </React.Fragment>
                      );
                    })}
                    ]
                  </span>
                )}
              </div>
            )}
            <div className="dayInfo-slot-otherInfo">
              <div className="dayInfo-slot-featuredRolesQuantity">
                {t('schedule.featuredShort')} {featuredRolesQuantity}{t('common.pipeSeparator')}{t('schedule.crowdShort')} {crowdRolesQuantity}
              </div>
              <div className="dayInfo-slot-pages">
                {totalPages.toFixed(1).replace(/\.0$/, '')} {t('schedule.pages')}
              </div>
            </div>
          </div>
        )}
        {/* 空日单元格：该日有受限日期的主要角色，显示在底部；渲染占位容器确保与有任务日相同布局位置 */}
        {!hasSoltSlot && restrictedDatesCastItemsForThisDate.length > 0 && (
          <div className="dayInfo-slot-bottom-info-stats">
            <div className="dayInfo-slot-cast dayInfo-slot-cast-empty-day">
              <span className="dayInfo-slot-cast-restrictedBracketGroup">
                [
                {restrictedDatesCastItemsForThisDate.map((item, i) => {
                  const restrictedNote = getRestrictedDateNote(item.elementRestrictedDates, dateStr);
                  return (
                  <React.Fragment key={item.elementID}>
                    {i > 0 && '/'}
                    <span
                      className="dayInfo-slot-cast-restrictedBracketItem"
                      title={restrictedNote || undefined}
                    >
                      {item.elementAbbr}
                    </span>
                  </React.Fragment>
                  );
                })}
                ]
              </span>
            </div>
            {/* 占位：保持与有任务日相同的 flex 布局，使受限日期容器位置一致 */}
            <div className="dayInfo-slot-otherInfo dayInfo-slot-otherInfo-placeholder" aria-hidden="true">
              <div className="dayInfo-slot-featuredRolesQuantity">&nbsp;</div>
              <div className="dayInfo-slot-pages">&nbsp;</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DayInfo;
